"use client";

import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  pushSupported,
  getSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  type PushTopic,
} from "@/lib/push-client";

/**
 * Opt-in / opt-out button for browser push notifications. Renders nothing if
 * the browser doesn't support Web Push. Reusable across topics — the public
 * blog uses "blog"; the admin header uses "admin" (message alerts).
 */
export function PushSubscribeButton({
  className,
  topic = "blog",
  label = "Notify me of new posts",
  subscribedLabel = "Unsubscribe",
}: {
  className?: string;
  topic?: PushTopic;
  label?: string;
  subscribedLabel?: string;
}) {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Feature detection is client-only, so support can't be known until mount.
    if (!pushSupported()) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSupported(true);
    getSubscription().then((s) => setSubscribed(!!s));
  }, []);

  if (!supported) return null;

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        setSubscribed(false);
      } else {
        await subscribeToPush(topic);
        setSubscribed(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={className}>
      <Button variant="outline" size="sm" onClick={toggle} disabled={busy}>
        {busy ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : subscribed ? (
          <BellOff className="mr-2 h-4 w-4" />
        ) : (
          <Bell className="mr-2 h-4 w-4" />
        )}
        {subscribed ? subscribedLabel : label}
      </Button>
      {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
    </div>
  );
}
